import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '../../components/button';
import { Badge, Card, CardBody, InlineRow, Muted, Stack, Strong } from '../../components/ui';
import {
  useAcceptJobMutation,
  useDeclineJobMutation,
  type IServiceJobBookingFieldsFragment,
} from '../../services/serviceService/__generated__/queries.generated';
import { formatMoney } from './priceTables';
import {
  countdownText,
  jobItemLabels,
  jobStatusView,
  windowText,
} from '../ServiceGuestPage/serviceJob';
import { stayErrorMessage } from '../StayHostPage/stayErrors';

export const providerJobPath = (bookingId: string) => `/services/provider/jobs/${bookingId}`;

/** One job on the Provider's Today page (W17, W18); a request has Accept and Decline. */
export function ProviderJobCard({ job }: { job: IServiceJobBookingFieldsFragment }) {
  const navigate = useNavigate();
  const refetchQueries = ['ProviderToday'];
  const [accept, { loading: accepting }] = useAcceptJobMutation({ refetchQueries });
  const [decline, { loading: declining }] = useDeclineJobMutation({ refetchQueries });
  const [error, setError] = useState<string | null>(null);
  const info = job.serviceJob;
  const status = jobStatusView(job);
  const isRequest = Boolean(info?.respondBy);

  const onAccept = async () => {
    setError(null);
    try {
      await accept({ variables: { bookingId: job.id } });
      toast.success('Job accepted');
    } catch (e) {
      setError(stayErrorMessage(e, 'Could not accept'));
    }
  };

  const onDecline = async () => {
    const reason =
      window.prompt('Why can you not take it? (optional, the customer sees this)') ?? undefined;
    if (reason === undefined) return;
    setError(null);
    try {
      await decline({ variables: { bookingId: job.id, reason: reason.trim() || null } });
      toast.success('Request declined');
    } catch (e) {
      setError(stayErrorMessage(e, 'Could not decline'));
    }
  };

  return (
    <Card data-testid={`provider-job-${job.id}`}>
      <CardBody>
        <Stack $gap={4}>
          <InlineRow style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div>
              <Strong>{jobItemLabels(info?.items).join(' + ') || 'Job'}</Strong>
              <div>
                <Muted $size="sm">
                  {info ? windowText(info.windowStartAt, info.windowEndAt, info.timezone) : ''}
                </Muted>
              </div>
              <div>
                <Muted $size="sm">
                  {[
                    info?.workerName,
                    job.guestDisplayName,
                    info?.site ? `${info.site.city} ${info.site.zip}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Muted>
              </div>
            </div>
            <Stack $gap={4}>
              <Badge tone={status.tone}>{status.label}</Badge>
              <Muted $size="sm">{formatMoney(Number(job.receipt.totalPrice ?? 0))}</Muted>
            </Stack>
          </InlineRow>
          {isRequest && info?.respondBy && (
            <Muted $size="sm">
              Answer {countdownText(Date.parse(info.respondBy))} or the request expires.
            </Muted>
          )}
          {error && <Muted $size="sm">{error}</Muted>}
          <InlineRow style={{ flexWrap: 'wrap' }}>
            {isRequest && (
              <>
                <Button
                  type="button"
                  size="sm"
                  text="Accept"
                  onClick={onAccept}
                  isLoading={accepting}
                  dataTestId={`job-accept-${job.id}`}
                />
                <Button
                  type="button"
                  theme="secondary"
                  size="sm"
                  text="Decline"
                  onClick={onDecline}
                  isLoading={declining}
                />
              </>
            )}
            <Button
              type="button"
              theme="text"
              size="sm"
              text="Open"
              onClick={() => navigate(providerJobPath(job.id))}
            />
          </InlineRow>
        </Stack>
      </CardBody>
    </Card>
  );
}
